import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { INTENTS, rolesFromIntents } from "@/lib/onboardingData";
import { useAuth } from "@/context/AuthContext";
import {
    OnboardingShell,
    OptionCard,
    GradientPrimaryButton,
} from "@/screens/onboarding/_ui";

const OnboardingIntents = () => {
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();
    // Per-user key — never bleed selections across accounts
    const STORAGE_KEY = `neara.onboarding.${user?.id || "anon"}`;

    const readProgress = () => {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
        } catch {
            return null;
        }
    };
    const writeProgress = (patch) => {
        const cur = readProgress() || {};
        const next = { ...cur, ...patch };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        return next;
    };

    const [selected, setSelected] = useState([]);
    const [otherText, setOtherText] = useState("");

    // Business accounts skip the personal "what brings you to Neyra" flow.
    useEffect(() => {
        if (user && user.accountType === "business") {
            navigate("/home", { replace: true });
        }
    }, [user, navigate]);

    // Load this user's previously saved selections on mount
    useEffect(() => {
        const cur = readProgress();
        if (cur?.intents) setSelected(cur.intents);
        if (cur?.intentOther) setOtherText(cur.intentOther);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const othersSelected = selected.includes("others");
    const canContinue =
        selected.length > 0 &&
        (!othersSelected || otherText.trim().length > 0);

    const toggle = (id) => {
        setSelected((prev) => {
            const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
            // Save immediately per prompt rule
            writeProgress({ intents: next });
            return next;
        });
    };

    const handleOtherChange = (v) => {
        setOtherText(v);
        writeProgress({ intentOther: v.trim() || null });
    };

    const handleContinue = () => {
        if (!canContinue) return;
        const roles = rolesFromIntents(selected);
        writeProgress({
            intents: selected,
            intentOther: othersSelected ? otherText.trim() || null : null,
            roles,
        });
        // Persist roles on the user immediately as well
        updateUser({ roles });
        navigate("/onboarding/interests");
    };

    return (
        <OnboardingShell
            testId="onboarding-intents-screen"
            step={1}
            total={2}
            title="What brings you to Neyra?"
            subtitle="Let's personalize your experience"
            footer={
                <GradientPrimaryButton
                    testId="onboarding-intents-continue"
                    onClick={handleContinue}
                    disabled={!canContinue}
                >
                    Continue
                </GradientPrimaryButton>
            }
        >
            <div
                className="grid grid-cols-2 gap-3 pt-4"
                data-testid="onboarding-intents-grid"
            >
                {INTENTS.map((i) => (
                    <OptionCard
                        key={i.id}
                        testId={`intent-${i.id}`}
                        icon={i.icon}
                        label={i.label}
                        selected={selected.includes(i.id)}
                        onClick={() => toggle(i.id)}
                        variant="row"
                    />
                ))}
            </div>

            {othersSelected && (
                <div className="mt-5 animate-slide-up" data-testid="onboarding-intent-other-wrap">
                    <label className="block">
                        <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                            Tell us what you're looking for
                        </span>
                        <span className="flex items-center h-14 px-4 rounded-2xl border border-border bg-secondary focus-within:border-primary focus-within:bg-background transition-colors">
                            <input
                                data-testid="onboarding-intent-other-input"
                                value={otherText}
                                onChange={(e) => handleOtherChange(e.target.value)}
                                placeholder="e.g. volunteer opportunities near me"
                                className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-muted-foreground/70"
                                maxLength={80}
                                autoFocus
                            />
                        </span>
                    </label>
                </div>
            )}

            <div className="h-6" />
        </OnboardingShell>
    );
};

export default OnboardingIntents;
